
import React from 'react';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Shield, Wrench, Cloud, Briefcase, Building } from 'lucide-react';

interface CategoryCardProps {
  category: {
    id: string;
    name: string;
    icon: string | null;
    description?: string;
  };
  subcategories?: {
    id: string;
    name: string;
    parentId: string;
    description?: string;
  }[];
  onSelectCategory: (categoryId: string) => void;
  onSelectSubcategory?: (subcategoryId: string) => void;
  setActiveTab: (tab: string) => void;
}

const CategoryCard: React.FC<CategoryCardProps> = ({ 
  category, 
  subcategories, 
  onSelectCategory, 
  onSelectSubcategory, 
  setActiveTab 
}) => {
  // Function to render the appropriate icon based on the icon name
  const renderIcon = (iconName: string | null) => {
    if (!iconName) return null;
    
    switch (iconName) {
      case 'Shield':
        return <Shield className="h-4 w-4" />;
      case 'Wrench':
        return <Wrench className="h-4 w-4" />;
      case 'Cloud':
        return <Cloud className="h-4 w-4" />;
      case 'Briefcase':
        return <Briefcase className="h-4 w-4" />;
      case 'Building':
        return <Building className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <Card className="transition-all hover:shadow-lg">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          {category.icon && renderIcon(category.icon)}
          <span>{category.name}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {category.description && <p className="text-gray-600 mb-4">{category.description}</p>}
        {category.id === "industry-specific" && subcategories && (
          <div className="mt-4">
            <h4 className="text-sm font-medium mb-2">Industry Focus:</h4>
            <div className="flex flex-wrap gap-2">
              {subcategories.map(sub => (
                <Link 
                  key={sub.id}
                  to="#"
                  onClick={(e) => {
                    e.preventDefault();
                    onSelectCategory("industry-specific");
                    if (onSelectSubcategory) {
                      onSelectSubcategory(sub.id);
                    }
                    setActiveTab("all-posts");
                  }}
                  className="text-xs bg-gray-100 hover:bg-gray-200 px-2 py-1 rounded-full text-itmgmt-dark-blue"
                >
                  {sub.name}
                </Link>
              ))}
            </div>
          </div>
        )}
      </CardContent>
      <CardFooter>
        <Button 
          variant="ghost" 
          className="text-itmgmt-medium-blue hover:text-itmgmt-dark-blue hover:bg-itmgmt-light-blue/20"
          onClick={() => {
            onSelectCategory(category.id);
            setActiveTab("all-posts");
          }}
        >
          Browse Articles
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CategoryCard;
